"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { GradientText } from "@/components/ui/gradient-text";
import { CheckCircle2 } from "lucide-react";

const tiers = [
  {
    name: "Free",
    price: "$0",
    description: "Perfect for casual listeners",
    features: [
      "1 Artist",
      "Email Notifications",
      "Basic Alerts",
      "Community Access"
    ]
  },
  {
    name: "Pro",
    price: "$9.99/mo",
    description: "For serious music enthusiasts",
    popular: true,
    features: [
      "3 Artists",
      "Email & SMS Notifications",
      "Instant Alerts",
      "Leak History",
      "Quality Filters",
      "Discord Integration"
    ]
  },
  {
    name: "Ultimate",
    price: "$24.99/mo",
    description: "For the true collectors",
    features: [
      "Unlimited Artists",
      "Priority Notifications",
      "Advanced Analytics",
      "API Access",
      "24/7 Support",
      "Custom Filters",
      "Early Access Features"
    ]
  }
];

export function PricingSection() {
  return (
    <section id="pricing" className="py-32 px-4 relative">
      <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:32px]" />
      <div className="container max-w-7xl relative">
        <div className="text-center mb-20">
          <h2 className="text-4xl font-bold mb-6">
            <GradientText>Choose Your Plan</GradientText>
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Select the perfect plan for your needs. Upgrade or downgrade anytime.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {tiers.map((tier, index) => (
            <Card key={index} className={`relative group transition-all duration-300 hover:shadow-xl hover:shadow-primary/5 ${
              tier.popular ? 'border-primary scale-105 hover:scale-[1.06]' : 'hover:scale-[1.02]'
            }`}>
              {tier.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-primary text-primary-foreground px-6 py-1.5 rounded-full text-sm font-medium">
                    Most Popular
                  </span>
                </div>
              )}
              <CardHeader className="space-y-2">
                <CardTitle className="text-2xl text-center">{tier.name}</CardTitle>
                <div className="text-4xl font-bold text-center mt-4">
                  <GradientText>{tier.price}</GradientText>
                </div>
                <p className="text-center text-muted-foreground mt-2">{tier.description}</p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-4 mb-8">
                  {tier.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <CheckCircle2 className="h-5 w-5 text-primary mr-3 shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  className={`w-full h-12 text-lg ${tier.popular ? 'bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90' : ''}`} 
                  variant={tier.popular ? "default" : "outline"}
                >
                  {tier.name === "Free" ? "Get Started" : "Subscribe Now"}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}